import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Badge, Card, EmptyState, SectionHeader } from "../../../components/ui";
import CentinelaWatchPill from "../components/CentinelaWatchPill";
import RefreshSectionButton from "../components/RefreshSectionButton";
import TabLoader from "../components/TabLoader";
import {
  useCentinelaCatalogs,
  useCentinelaUsage,
} from "../../../features/centinela/queries";
import { mapCentinelaUsage } from "../services/mapCentinelaUsage";
import { CALL_PURPOSE_CHART_COLORS, CENTINELA_CALL_TYPE_ICONS } from "../theme";

const DONUT_RADIUS = 15.9;
const DONUT_CIRCUMFERENCE = 100;

const GROUP_CHART_COLORS: Record<string, string> = {
  rutina: CALL_PURPOSE_CHART_COLORS.routineAndCustody,
  alertas: CALL_PURPOSE_CHART_COLORS.alerts,
  entregas: CALL_PURPOSE_CHART_COLORS.deliveries,
};

const METRIC_APPLICATION_TONES = {
  soft: "warning",
  hard: "error",
  metric: "neutral",
} as const;

const FALLBACK_CALL_TYPE_ICON = "ri-phone-line";

const FALLBACK_METRIC_ICON = "ri-bar-chart-2-line";

const SECONDARY_METRIC_ICONS: Record<string, string> = {
  unit_month: "ri-truck-line",
  geofence: "ri-shape-line",
  trip_custody: "ri-shield-check-line",
  call: "ri-phone-line",
};

const BY_TYPE_BAR_TONES = [
  "bg-primary-500",
  "bg-error",
  "bg-info",
  "bg-warning",
  "bg-success",
] as const;

export default function UsoConsumoSection() {
  const { t, i18n } = useTranslation("common");

  const usageQuery = useCentinelaUsage();
  const catalogsQuery = useCentinelaCatalogs();

  const { usage, breakdown, hasData } = useMemo(
    () => mapCentinelaUsage(usageQuery.data),
    [usageQuery.data],
  );

  const callTypeLabels = useMemo(() => {
    const rows = catalogsQuery.data?.call_types ?? [];
    return new Map(rows.map((row) => [row.id, row.label]));
  }, [catalogsQuery.data]);

  const headline = usage.protagonista;

  const numberFormat = (value: number) => value.toLocaleString(i18n.language);

  const currencyFormat = (value: number) =>
    value.toLocaleString(i18n.language, {
      style: "currency",
      currency: "MXN",
      maximumFractionDigits: 0,
    });

  const dayLabel = (iso: string) =>
    new Date(iso).toLocaleDateString(i18n.language, { day: "2-digit", month: "short" });

  const callsPercent = Math.min(100, Math.round(headline?.pct ?? 0));

  const periodLabel = usage.period
    ? new Date(`${usage.period}-01T00:00:00`).toLocaleDateString(i18n.language, {
        month: "long",
        year: "numeric",
      })
    : "";

  const purposeTotal = breakdown.grupos.reduce((total, group) => total + group.llamadas, 0);

  const purposeSegments = breakdown.grupos.reduce<
    { id: string; color: string; length: number; offset: number }[]
  >((segments, group, index) => {
    if (group.llamadas === 0) return segments;

    const previous = segments.reduce((total, segment) => total + segment.length, 0);
    return [
      ...segments,
      {
        id: group.id,
        color:
          GROUP_CHART_COLORS[group.id] ??
          Object.values(GROUP_CHART_COLORS)[index % 3],
        length: (group.llamadas / Math.max(purposeTotal, 1)) * DONUT_CIRCUMFERENCE,
        offset: -previous,
      },
    ];
  }, []);

  const byTypeTotal = breakdown.por_tipo.reduce((total, item) => total + item.llamadas, 0);
  const maxTypeCount = Math.max(...breakdown.por_tipo.map((item) => item.llamadas), 1);
  const maxUnitCalls = Math.max(...breakdown.top_unidades.map((item) => item.llamadas), 1);
  const maxVoiceMinutes = Math.max(
    ...(breakdown.voz?.por_dia ?? []).map((point) => point.minutos),
    1,
  );

  return (
    <div className="flex h-full w-full flex-col">
      <SectionHeader
        eyebrow={<CentinelaWatchPill />}
        title={t("monitoreoIa.usage.title")}
        subtitle={t("monitoreoIa.usage.subtitle")}
        actions={
          <RefreshSectionButton
            loading={usageQuery.isFetching || catalogsQuery.isFetching}
            onRefresh={() => {
              void usageQuery.refetch();
              void catalogsQuery.refetch();
            }}
          />
        }
      />

      <div className="flex-1 overflow-y-auto p-3 sm:p-6">
        <div className="mx-auto w-full max-w-6xl space-y-5">
          {usageQuery.isLoading ? (
            <TabLoader label={t("monitoreoIa.usage.loading")} />
          ) : !hasData ? (
            <EmptyState icon="ri-bar-chart-2-line" title={t("monitoreoIa.usage.empty")} />
          ) : (
            <>
            <Card className="space-y-3">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
                <div className="flex flex-wrap items-center gap-2">
                  <b className="text-[13px] text-content">{t("monitoreoIa.usage.callsTitle")}</b>
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-primary-500/30 bg-primary-500/10 px-2 py-0.5 text-[10px] max-sm:text-[11px] font-bold text-primary-500">
                    <i className="ri-shield-check-line text-[10px]" />
                    {t("monitoreoIa.usage.modelBase", {
                      model: usage.modelo,
                      price: currencyFormat(usage.precio_base),
                    })}
                  </span>
                </div>

                <div className="flex items-baseline gap-1 sm:ml-auto">
                  <span className="text-[24px] font-extrabold tabular-nums text-primary-500">
                    {numberFormat(headline?.usado ?? 0)}
                  </span>
                  <span className="text-xs text-content-secondary">
                    {headline?.tope
                      ? t("monitoreoIa.usage.ofQuota", {
                          total: numberFormat(headline.tope),
                          label: headline.etiqueta.plural,
                          period: periodLabel,
                        })
                      : t("monitoreoIa.usage.inPeriod", {
                          label: headline?.etiqueta.plural ?? "",
                          period: periodLabel,
                        })}
                  </span>
                </div>
              </div>

              <div className="h-2.5 w-full overflow-hidden rounded-full bg-subtle">
                <div
                  className="h-full rounded-full bg-primary-500 transition-all"
                  style={{ width: `${callsPercent}%` }}
                />
              </div>

              <div className="flex flex-wrap items-center justify-between gap-2 text-[11px] text-content-secondary">
                <span>{t("monitoreoIa.usage.quotaUsed", { percent: callsPercent })}</span>
                {headline && headline.precio_unitario > 0 ? (
                  <span>
                    {t("monitoreoIa.usage.extraUnit", {
                      price: currencyFormat(headline.precio_unitario),
                      label: headline.etiqueta.singular,
                    })}
                  </span>
                ) : null}
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <p className="rounded-xl bg-surface p-3 text-xs leading-relaxed text-content-secondary">
                  <i className="ri-bar-chart-box-line mr-1.5 text-info" />
                  {headline?.proyeccion
                    ? t("monitoreoIa.usage.projectionText", {
                        estimate: numberFormat(headline.proyeccion.cierre_estimado),
                        label: headline.etiqueta.plural,
                        progress: usage.avance_periodo_pct,
                      })
                    : t("monitoreoIa.usage.projectionMissing")}
                  {headline?.proyeccion && !headline.proyeccion.confiable
                    ? ` ${t("monitoreoIa.usage.projectionUnreliable")}`
                    : ""}
                </p>

                <p className="rounded-xl bg-surface p-3 text-xs leading-relaxed text-content-secondary">
                  <i className="ri-money-dollar-circle-line mr-1.5 text-primary-500" />
                  {t("monitoreoIa.usage.amountText", {
                    total: currencyFormat(usage.importe.total),
                    extra: currencyFormat(usage.importe.extra),
                    projected: currencyFormat(usage.importe.proyectado_cierre),
                  })}
                </p>
              </div>

              {headline?.aviso ? (
                <p className="rounded-xl border border-warning/40 bg-warning/10 p-3 text-xs text-content">
                  <i className="ri-alert-line mr-1.5 text-warning" />
                  {headline.aviso}
                </p>
              ) : null}
            </Card>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              <Card>
                <div className="flex items-center justify-between gap-2">
                  <b className="text-[13px] text-content">{t("monitoreoIa.usage.purposeTitle")}</b>
                  <span className="text-[18px] font-extrabold tabular-nums text-primary-500">
                    {numberFormat(purposeTotal)}
                  </span>
                </div>
                <p className="mb-4 text-[11px] text-content-secondary">
                  {t("monitoreoIa.usage.purposeSubtitle")}
                </p>

                <div className="flex items-center gap-4">
                  <div className="relative h-16 w-16 shrink-0">
                    <svg viewBox="0 0 36 36" className="h-full w-full -rotate-90">
                      <circle
                        cx="18"
                        cy="18"
                        r={DONUT_RADIUS}
                        fill="none"
                        stroke="rgb(var(--c-subtle))"
                        strokeWidth="4"
                      />
                      {purposeSegments.map((segment) => (
                        <circle
                          key={segment.id}
                          cx="18"
                          cy="18"
                          r={DONUT_RADIUS}
                          fill="none"
                          stroke={segment.color}
                          strokeWidth="4"
                          strokeLinecap="round"
                          strokeDasharray={`${segment.length} ${DONUT_CIRCUMFERENCE}`}
                          strokeDashoffset={segment.offset}
                        />
                      ))}
                    </svg>
                    <span className="absolute inset-0 flex items-center justify-center text-[13px] font-extrabold text-content">
                      {numberFormat(purposeTotal)}
                    </span>
                  </div>

                  <ul className="flex flex-col gap-2 text-[11px]">
                    {breakdown.grupos.map((group, index) => (
                      <li key={group.id} className="flex items-center gap-2">
                        <span
                          className="h-2.5 w-2.5 shrink-0 rounded-sm"
                          style={{
                            background:
                              GROUP_CHART_COLORS[group.id] ??
                              Object.values(GROUP_CHART_COLORS)[index % 3],
                          }}
                        />
                        <span className="text-content-secondary">
                          {group.etiqueta} · {numberFormat(group.llamadas)}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </Card>

              <Card>
                <div className="flex items-center justify-between gap-2">
                  <b className="text-[13px] text-content">{t("monitoreoIa.usage.byTypeTitle")}</b>
                  <span className="text-[18px] font-extrabold tabular-nums text-info">
                    {numberFormat(byTypeTotal)}
                  </span>
                </div>
                <p className="mb-4 text-[11px] text-content-secondary">
                  {t("monitoreoIa.usage.byTypeSubtitle", { count: breakdown.por_tipo.length })}
                </p>

                <ul className="space-y-3">
                  {breakdown.por_tipo.map((item, index) => (
                    <li key={item.tipo} className="flex items-center gap-3">
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary-500/10">
                        <i
                          className={`${CENTINELA_CALL_TYPE_ICONS[item.tipo] ?? FALLBACK_CALL_TYPE_ICON} text-sm text-primary-500`}
                        />
                      </span>

                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-xs font-semibold text-content">
                          {callTypeLabels.get(item.tipo) ?? item.tipo}
                        </span>
                        <span className="mt-1 block h-1.5 w-full overflow-hidden rounded-full bg-subtle">
                          <span
                            className={`block h-full rounded-full ${BY_TYPE_BAR_TONES[index % BY_TYPE_BAR_TONES.length]}`}
                            style={{ width: `${Math.max((item.llamadas / maxTypeCount) * 100, 6)}%` }}
                          />
                        </span>
                      </span>

                      <span className="shrink-0 text-[15px] font-extrabold tabular-nums text-content">
                        {numberFormat(item.llamadas)}
                      </span>
                    </li>
                  ))}
                </ul>
              </Card>

              <Card>
                <div className="flex items-center justify-between gap-2">
                  <b className="text-[13px] text-content">{t("monitoreoIa.usage.topUnitsTitle")}</b>
                  <span className="text-xs font-bold text-info">
                    {breakdown.voz
                      ? t("monitoreoIa.usage.voiceAverage", {
                          value: breakdown.voz.promedio_por_llamada,
                        })
                      : null}
                  </span>
                </div>
                <p className="mb-4 text-[11px] text-content-secondary">
                  {t("monitoreoIa.usage.topUnitsSubtitle", {
                    count: breakdown.top_unidades.length,
                  })}
                </p>

                <ul className="space-y-3">
                  {breakdown.top_unidades.map((row) => (
                    <li key={row.device_id} className="flex items-center gap-3">
                      <span className="w-[72px] shrink-0 truncate text-[10.5px] font-semibold max-sm:w-24 max-sm:text-[11px] leading-tight text-content-secondary">
                        {row.nombre}
                      </span>

                      <span className="h-2 flex-1 overflow-hidden rounded-full bg-subtle">
                        <span
                          className={`block h-full rounded-full ${
                            row.llamadas > 4
                              ? "bg-primary-500"
                              : row.llamadas > 0
                                ? "bg-info"
                                : "bg-line"
                          }`}
                          style={{ width: `${Math.max((row.llamadas / maxUnitCalls) * 100, 4)}%` }}
                        />
                      </span>

                      <span className="w-14 shrink-0 text-right text-[11px] font-bold text-content-secondary">
                        {row.llamadas > 0 ? `${numberFormat(row.llamadas)} · ${row.tiempo}` : "—"}
                      </span>
                    </li>
                  ))}
                </ul>
              </Card>
            </div>

            <Card>
              <div className="mb-4 flex flex-wrap items-center gap-2">
                <i className="ri-file-list-3-line text-sm text-content-faint" />
                <b className="text-[13px] text-content">{t("monitoreoIa.usage.periodTitle")}</b>
                <span className="text-xs text-content-secondary">
                  {t("monitoreoIa.usage.periodSubtitle")}
                </span>
              </div>

              <ul className="divide-y divide-line-soft">
                {usage.secundarios.map((metric) => (
                  <li key={metric.kind} className="flex items-center gap-3 py-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-surface">
                      <i className={`${SECONDARY_METRIC_ICONS[metric.kind] ?? FALLBACK_METRIC_ICON} text-sm text-content-secondary`} />
                    </span>

                    <div className="min-w-0 flex-1">
                      <p className="text-[12.5px] font-semibold text-content">
                        {metric.etiqueta.plural}
                      </p>
                      <p className="mt-0.5 text-[11px] text-content-secondary">
                        {metric.tope !== null
                          ? t("monitoreoIa.usage.metricWithCap", {
                              cap: numberFormat(metric.tope),
                              remaining: numberFormat(metric.restante ?? 0),
                            })
                          : t("monitoreoIa.usage.metricNoCap")}
                        {metric.proyeccion
                          ? ` · ${t("monitoreoIa.usage.metricProjection", {
                              estimate: numberFormat(metric.proyeccion.cierre_estimado),
                            })}`
                          : ""}
                      </p>
                    </div>

                    <div className="shrink-0 text-right">
                      <p className="text-[13px] font-extrabold tabular-nums text-content">
                        {numberFormat(metric.usado)}
                      </p>
                      <p className="mt-0.5">
                        {metric.cobrable && metric.precio_unitario > 0 ? (
                          <span className="text-[10px] max-sm:text-[11px] text-content-faint">
                            {t("monitoreoIa.usage.perUnitPrice", {
                              price: currencyFormat(metric.precio_unitario),
                            })}
                          </span>
                        ) : (
                          <Badge tone={METRIC_APPLICATION_TONES[metric.aplicacion]} size="sm">
                            {t(`monitoreoIa.usage.applications.${metric.aplicacion}`)}
                          </Badge>
                        )}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
              <p className="mt-3 border-t border-line-soft pt-3 text-[11px] text-content-secondary">
                {t("monitoreoIa.usage.auditNotice")}
              </p>
            </Card>

            {breakdown.voz ? (
              <Card>
                <div className="mb-1 flex flex-wrap items-center gap-2">
                  <i className="ri-mic-line text-sm text-content-faint" />
                  <b className="text-[13px] text-content">{t("monitoreoIa.usage.voiceTitle")}</b>
                  <span className="ml-auto text-[18px] font-extrabold tabular-nums text-primary-500">
                    {t("monitoreoIa.usage.voiceMinutes", {
                      count: breakdown.voz.minutos_totales,
                    })}
                  </span>
                </div>
                <p className="mb-4 text-[11px] text-content-secondary">
                  {t("monitoreoIa.usage.voiceSubtitle", {
                    average: breakdown.voz.promedio_por_llamada,
                  })}
                </p>

                <div className="flex items-end gap-1.5 overflow-x-auto pb-1">
                  {breakdown.voz.por_dia.map((point) => (
                    <div
                      key={point.dia}
                      className="flex min-w-[26px] flex-1 flex-col items-center gap-1"
                    >
                      <span className="text-[9px] max-sm:text-[10px] tabular-nums text-content-faint">
                        {point.minutos > 0 ? point.minutos : ""}
                      </span>
                      <span className="flex h-16 w-full items-end justify-center rounded-md bg-subtle">
                        <span
                          className="w-full rounded-md bg-primary-500 transition-all"
                          style={{
                            height: `${Math.max((point.minutos / maxVoiceMinutes) * 100, 4)}%`,
                          }}
                        />
                      </span>
                      <span className="text-[9px] max-sm:text-[10px] text-content-faint">
                        {dayLabel(`${point.dia}T00:00:00`)}
                      </span>
                    </div>
                  ))}
                </div>
              </Card>
            ) : null}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
